<template>
  <div class="space-y-6">
    <credit-score
      v-for="score in creditReportRecords"
      :key="score.Score"
      :score="score"
      data-cy="credit-score"
    />
    <trade-summary-section
      v-for="(summary, index) in tradeSummary"
      :key="index"
      :trade-summary="summary"
      title="Trade Summary"
      data-cy="trade-summary"
    />
    <top-business-record-section
      v-for="(record, index) in topBusinessRecords"
      ref="topBusinessRecordRef"
      :key="index"
      :business-record="record"
      :count="topBusinessRecordsCount"
      :opened-articles="openedArticles[index]"
      data-cy="top-business-record"
      @toggle="handleToggle($event, index, true)"
    />
    <trade-summary-section
      v-for="(summary, index) in b2bTradeSummary"
      :key="index"
      :trade-summary="summary"
      title="B2B Trade Summary"
      data-cy="b2b-trade-summary"
    />
    <small-business-attributes
      v-if="attributesGroups"
      :attributes-group="attributesGroups"
      data-cy="small-business-attributes"
      :opened-attributes="openedAttributes"
      @toggle="($event) => handleToggle($event.section, $event.index)"
      @all-sections:init="initAllAttributes"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";

import CreditScore from "@/components/deals/dataServices/businessCredit/lexisNexis/CreditScore.vue";
import SmallBusinessAttributes from "@/components/deals/dataServices/businessCredit/lexisNexis/SmallBusinessAttributes.vue";
import TopBusinessRecordSection from "@/components/deals/dataServices/businessCredit/lexisNexis/TopBusinessRecord.vue";
import TradeSummarySection from "@/components/deals/dataServices/businessCredit/lexisNexis/TradeSummary.vue";

import type {
  BusinessCreditScoreWithDate,
  SmallBusinessBipCombinedReportResponseEx,
} from "@/models/commercialData/lexisnexis/LexisNexisSmallBusinessBlendedCreditScore";

const props = defineProps<{
  data: SmallBusinessBipCombinedReportResponseEx;
}>();

const openedArticles = reactive<Record<string, boolean>[]>([]);
const openedAttributes = reactive<Record<string, boolean>[]>([]);

const records = computed(
  () => props.data.response?.CreditReportRecords.Record ?? []
);

const creditReportRecords = computed<BusinessCreditScoreWithDate[]>(() => {
  const allScores: BusinessCreditScoreWithDate[] = [];
  for (const record of records.value) {
    const scorings = record.Scorings?.BusinessCreditScoring ?? [];
    const dateScored = scorings[0].DateScored ?? {};
    for (const scoring of scorings) {
      const scores = scoring.Scores?.BusinessCreditScore ?? [];
      for (const score of scores) {
        allScores.push({ ...score, dateScored });
      }
    }
  }
  return allScores;
});

const tradeSummary = computed(() =>
  records.value.map(({ TradeSummary }) => TradeSummary)
);

const b2bTradeSummary = computed(() =>
  records.value.map(({ B2BTradeData }) => B2BTradeData.TradeSummary)
);

const topBusinessRecords = computed(() =>
  records.value.map(({ TopBusinessRecord }) => TopBusinessRecord)
);

const attributesGroups = computed(
  () => props.data.response?.SmallBusinessAnalyticsResults?.AttributesGroups
);

const topBusinessRecordsCount = computed(() => {
  return topBusinessRecords.value.reduce((acc, record) => {
    return (
      acc +
      Object.values(record).reduce((acc, value) => {
        return acc + sumSectionValues(value);
      }, 0)
    );
  }, 0);
});

const sumSectionValues = (section: Record<string, string>): number => {
  return Object.values(section).reduce((acc, value) => {
    if (typeof value === "object") {
      return acc + sumSectionValues(value);
    } else {
      return Number(value) + acc;
    }
  }, 0);
};

const toggleAllSections = (open: boolean) => {
  Object.values(topBusinessRecords.value).map((record, index) => {
    openedArticles[index] = {};
    openedArticles[index]["TopBusinessRecord"] = open;
    Object.keys(record).forEach((record) => {
      openedArticles[index][record] = open;
    });
  });
  openedAttributes.forEach((record, index) => {
    Object.keys(record).forEach((section) => {
      openedAttributes[index][section] = open;
    });
  });
};

const initAllAttributes = (sections: string[]) => {
  Object.values(
    props.data.response?.SmallBusinessAnalyticsResults?.AttributesGroups || {}
  ).forEach((_, index) => {
    openedAttributes[index] = {};
    sections.forEach((section) => {
      openedAttributes[index][section] = true;
    });
  });
};

const handleToggle = (key: string, index: number, isArticles = false) => {
  if (isArticles) {
    openedArticles[index][key] = !openedArticles[index][key];
    return;
  }
  openedAttributes[index][key] = !openedAttributes[index][key];
};

toggleAllSections(true);

defineExpose({ toggleAllSections });
</script>
